import zipcodes from "zipcodes";
import type { FTLRequiredFields, RequiredFields } from "@src/types/api";

export const lookupStateFromZip = (zip: string): string => {
  if (!zip || zip.length !== 5) return "";
  try {
    const locality = zipcodes.lookup(zip);

    return locality?.state || "";
  } catch {
    return "";
  }
};
export const lookupCityStateFromZip = (zip: string): string => {
  const five = String(zip ?? "")
    .trim()
    .replace(/-.*$/, "")
    .substring(0, 5);

  if (five.length !== 5) return "";
  try {
    const loc = zipcodes.lookup(five);

    if (loc?.city && loc?.state) return `${loc.city}, ${loc.state}`;
    if (loc?.state) return loc.state;
    return "";
  } catch {
    return "";
  }
};
export type LocationDisplay = {
  line1: string;
  line2: string;
};
export function toLocationDisplay(cityState: string, zip: string): LocationDisplay {
  if (cityState) return { line1: cityState, line2: zip };
  return { line1: zip || "N/A", line2: "" };
}

type RawAddress = {
  zip: string;
  state?: string;
  city?: string;
};

function enrichAddress(raw: RawAddress): {
  zip: string;
  state: string;
  cityState: string;
} {
  const zip = String(raw.zip ?? "").trim();
  let state = (raw.state?.trim() ?? "") || (zip ? lookupStateFromZip(zip) : "");
  const rawCity = raw.city?.trim() ?? "";
  const city = rawCity.toLowerCase() === "unknown" ? "" : rawCity;
  let cityState = city && state ? `${city}, ${state}`.trim() : "";

  if (!cityState && zip) cityState = lookupCityStateFromZip(zip);
  return { zip, state, cityState };
}

export type OriginDestination = {
  originZip: string;
  originState: string;
  destinationZip: string;
  destinationState: string;
  originCityState: string;
  destinationCityState: string;
};

function getOriginDestination(origin: RawAddress, destination: RawAddress): OriginDestination {
  const o = enrichAddress(origin);
  const d = enrichAddress(destination);

  return {
    originZip: o.zip,
    originState: o.state,
    destinationZip: d.zip,
    destinationState: d.state,
    originCityState: o.cityState,
    destinationCityState: d.cityState,
  };
}

export function getFTLOriginDestinationFromFields(rf: FTLRequiredFields): OriginDestination {
  const stops = rf.stops ?? [];
  const first = stops[0] ?? null;
  const last = stops.length > 1 ? stops[stops.length - 1]! : first;
  const origin: RawAddress = {
    zip: rf.shipperZip ?? first?.zip ?? "",
    state: rf.shipperState ?? first?.state,
    city: rf.shipperCity ?? first?.city,
  };
  const destination: RawAddress = {
    zip: rf.consigneeZip ?? last?.zip ?? "",
    state: rf.consigneeState ?? last?.state,
    city: rf.consigneeCity ?? last?.city,
  };

  return getOriginDestination(origin, destination);
}
export function getLTLOriginDestinationFromFields(rf: RequiredFields): OriginDestination {
  const oa = rf.originAddress?.address;
  const da = rf.destinationAddress?.address;
  const origin: RawAddress = {
    zip: rf.shipperZip ?? oa?.postalCode ?? "",
    state: rf.shipperState ?? oa?.region ?? undefined,
    city: oa?.locality ?? undefined,
  };
  const destination: RawAddress = {
    zip: rf.consigneeZip ?? da?.postalCode ?? "",
    state: rf.consigneeState ?? da?.region ?? undefined,
    city: da?.locality ?? undefined,
  };

  return getOriginDestination(origin, destination);
}
